/* @jsxImportSource @emotion/react */
// this is needed to reach css styles in tests
import {
  Dispatch,
  InputHTMLAttributes,
  ReactNode,
  SetStateAction,
  forwardRef,
  useState,
} from 'react';
import { FieldErrors, RegisterOptions, useFormContext } from 'react-hook-form';

import { useOutsideClick } from '~src/commons/utils/hooks';
import { useFocus, useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useFormFieldScroll } from '~src/commons/utils/hooks/use-form-field-scroll';
import { InputError } from '~src/ui/input-error';
import * as inputStyles from '~src/ui/text-input.style';
import { styles } from '~src/ui/text-input.style';

import { AddressFormFields } from '../types';
import { useAssertedRef } from '../utils/use-asserted-ref';

const getErrorObject = (name: keyof AddressFormFields, errors?: FieldErrors<AddressFormFields>) => {
  if (!errors) return undefined;

  return name === 'address_lines' ? errors?.[name]?.[0] : errors[name];
};

type ExtensionElementProps = {
  isFocused: boolean;
  setFocused: Dispatch<SetStateAction<boolean>>;
  name: StandardInputProps['name'];
  hasValue: boolean;
};

export interface StandardInputProps extends InputHTMLAttributes<HTMLInputElement> {
  rightElement?: (props: ExtensionElementProps) => ReactNode;
  name: keyof AddressFormFields;
  label?: string;
  raiseLabelStyle?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  textarea?: boolean;
  warning?: boolean;
  dataTest?: string;
  isDisabled?: boolean;
  validationRules?: RegisterOptions;
  onInputClick?: () => void;
}

export const StandardInput = forwardRef<HTMLInputElement, StandardInputProps>(
  (
    {
      name,
      wrapperStyle,
      warning,
      isDisabled,
      dataTest,
      label,
      validationRules,
      rightElement,
      onBlur,
      onChange,
      onInputClick,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const { focusProps, isFocused } = useFocus();

    const assertedRef = useAssertedRef(ref);
    useFormFieldScroll(name, assertedRef);

    const {
      register,
      getValues,
      formState: { errors },
    } = useFormContext<AddressFormFields>();
    /**
     * address_lines is an array input and right now only the first value
     * of said array is used. Doin dirty check here because of the react-hook-form
     * types evaluation
     */
    const normalisedName = name === 'address_lines' ? 'address_lines.0' : name;

    const { ref: inputRef, ...restRegisterMethods } = register(normalisedName, {
      ...validationRules,
      /**
       * Additional functions to be invoked during change/blur handlers
       * Used for finite state machine signals in autocomplete input
       */
      onBlur: e => {
        setFocused(false);
        onBlur?.(e);
      },
      onChange: e => {
        onChange?.(e);
      },
    });
    const isLabelRaised = focused || !!getValues(normalisedName);

    const errObj = getErrorObject(name, errors);

    const hasError = Boolean(errObj) && errObj?.type !== 'warning';
    const hasWarning = warning || errObj?.type === 'warning';

    const handleOutsideClick = () => {
      if (!focusProps) {
        return;
      }
      focusProps.onBlur();
    };

    useOutSideIframeClick(() => {
      handleOutsideClick();
    }, [isFocused]);

    const wrapperRef = useOutsideClick(() => {
      handleOutsideClick();
    }, [isFocused]);

    const wordCountElement =
      props.maxLength !== undefined && isLabelRaised ? (
        <span css={styles.wordCount}>
          {getValues(name)?.length}/{20}
        </span>
      ) : undefined;

    const labelElement = (
      <label
        data-test="label"
        aria-hidden={hasError ? 'false' : 'true'}
        htmlFor={name}
        css={inputStyles.labelCss(isLabelRaised, hasError, hasWarning, isDisabled)}
      >
        <span role={hasError ? 'alert' : undefined}>{label}</span>
        {wordCountElement}
      </label>
    );

    const rightExtensionElement = () => {
      const rightElementProps = {
        isFocused,
        setFocused,
        onBlur: focusProps.onBlur,
        name,
        hasValue: !!getValues(name),
      };

      if (rightElement) {
        return <div css={inputStyles.extensionStyles}>{rightElement?.(rightElementProps)}</div>;
      }

      return null;
    };

    const handleOnClick = () => {
      onInputClick?.();
    };

    return (
      <>
        <div
          data-test="standard-input-wrapper"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          ref={wrapperRef}
          css={inputStyles.styles.wrapper(focused, hasError, hasWarning, false, isDisabled)}
          style={wrapperStyle}
        >
          <div css={inputStyles.inputAndLabelWrapper}>
            {labelElement}
            <input
              css={inputStyles.inputCss(false, isDisabled)} // TODO create textarea case
              ref={e => {
                if (typeof assertedRef === 'function') {
                  assertedRef(e);
                } else if (!!assertedRef) {
                  assertedRef.current = e;
                }
                inputRef(e);
              }}
              onFocus={() => {
                focusProps.onFocus();
              }}
              onClick={handleOnClick}
              readOnly={isDisabled}
              tabIndex={isDisabled ? -1 : undefined}
              data-test={dataTest}
              aria-label={label}
              {...restRegisterMethods}
              {...props}
            />
          </div>
          {!isDisabled && rightExtensionElement()}
        </div>
        {!!errObj && (
          <InputError dataTest={`${name}-error`} isSoftError={errObj?.type === 'warning'}>
            {errObj?.message}
          </InputError>
        )}
      </>
    );
  }
);
